import { createClient } from '@/lib/supabase/client';

export type CategoryType = 'kuenstler' | 'versorgung' | 'handel' | 'handwerk' | 'animation' | 'heerlager';

export const CATEGORY_LABELS: Record<CategoryType, string> = {
  kuenstler: 'Künstler',
  versorgung: 'Versorgung',
  handel: 'Handel',
  handwerk: 'Handwerk',
  animation: 'Animation',
  heerlager: 'Heerlager',
};

export const CATEGORY_ICONS: Record<CategoryType, string> = {
  kuenstler: '🎨',
  versorgung: '🍖',
  handel: '🛒',
  handwerk: '🔨',
  animation: '🎭',
  heerlager: '⚔️',
};

export const CATEGORY_DESCRIPTIONS: Record<CategoryType, string> = {
  kuenstler: 'Straßenkünstler, Gaukler und fahrende Leute',
  versorgung: 'Speisen, Getränke und Verpflegung',
  handel: 'Händler, Stände und Marktleute',
  handwerk: 'Handwerker und Vorführungen',
  animation: 'Animationen, Spiele und Mitmachaktionen',
  heerlager: 'Heerlager, Lager und Schaukämpfer',
};

export interface CategoryEntry {
  id: string;
  name: string;
  category: CategoryType;
  description?: string;
  location?: string;
  openTime?: string;
  closeTime?: string;
  tags?: string;
  eventId: string;
}

// Convert DB row (snake_case) to CategoryEntry (camelCase)
function rowToEntry(row: any): CategoryEntry {
  return {
    id: row.id,
    name: row.name,
    category: row.category as CategoryType,
    description: row.description || undefined,
    location: row.location || undefined,
    openTime: row.open_time || undefined,
    closeTime: row.close_time || undefined,
    tags: row.tags || undefined,
    eventId: row.event_id,
  };
}

export async function loadCategoryEntriesForEvent(eventId: string): Promise<CategoryEntry[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('category_entries')
    .select('*')
    .eq('event_id', eventId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error loading category entries:', error.message);
    return [];
  }
  return (data || []).map(rowToEntry);
}

export async function loadCategoryEntriesForCategory(eventId: string, category: CategoryType): Promise<CategoryEntry[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('category_entries')
    .select('*')
    .eq('event_id', eventId)
    .eq('category', category)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error loading category entries:', error.message);
    return [];
  }
  return (data || []).map(rowToEntry);
}

export async function addCategoryEntry(entry: Omit<CategoryEntry, 'id'>): Promise<CategoryEntry | null> {
  const supabase = createClient();
  const id = `${entry.category}-${Date.now()}`;
  const { data, error } = await supabase
    .from('category_entries')
    .insert({
      id,
      name: entry.name,
      category: entry.category,
      description: entry.description || null,
      location: entry.location || null,
      open_time: entry.openTime || null,
      close_time: entry.closeTime || null,
      tags: entry.tags || null,
      event_id: entry.eventId,
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding category entry:', error.message);
    return null;
  }
  return rowToEntry(data);
}

export async function updateCategoryEntry(id: string, entry: Omit<CategoryEntry, 'id' | 'eventId'>): Promise<CategoryEntry | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('category_entries')
    .update({
      name: entry.name,
      category: entry.category,
      description: entry.description || null,
      location: entry.location || null,
      open_time: entry.openTime || null,
      close_time: entry.closeTime || null,
      tags: entry.tags || null,
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating category entry:', error.message);
    return null;
  }
  return rowToEntry(data);
}

export async function deleteCategoryEntry(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase
    .from('category_entries')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting category entry:', error.message);
    return false;
  }
  return true;
}

export async function getAllPreviousNamesFromDB(category: CategoryType, currentEventId: string): Promise<string[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('category_entries')
    .select('name')
    .eq('category', category)
    .neq('event_id', currentEventId);

  if (error) {
    console.error('Error loading previous names:', error.message);
    return [];
  }
  const unique = Array.from(new Set((data || []).map((r: any) => r.name)));
  return unique;
}

// ---- Schedule helpers ----

export async function loadScheduleFromDB(eventId: string): Promise<any[] | null> {
  const supabase = createClient();

  const attempt = async () => {
    const { data, error } = await supabase
      .from('admin_schedule')
      .select('schedule_data')
      .eq('event_id', eventId)
      .maybeSingle();
    return { data, error };
  };

  let { data, error } = await attempt();

  // Retry once after 2s if schema cache error (table just created)
  if (error && (error.message?.includes('schema cache') || error.code === 'PGRST204' || error.code === '42P01')) {
    await new Promise(r => setTimeout(r, 2000));
    ({ data, error } = await attempt());
  }

  if (error) {
    console.error('Error loading schedule:', error.message);
    return null;
  }
  return data?.schedule_data ?? null;
}

export async function saveScheduleToDB(eventId: string, scheduleData: any[]): Promise<boolean> {
  const supabase = createClient();

  const attempt = async () => {
    const { error } = await supabase
      .from('admin_schedule')
      .upsert({ event_id: eventId, schedule_data: scheduleData, updated_at: new Date().toISOString() }, { onConflict: 'event_id' });
    return { error };
  };

  let { error } = await attempt();

  // Retry once after 2s if schema cache error
  if (error && (error.message?.includes('schema cache') || error.code === 'PGRST204' || error.code === '42P01')) {
    await new Promise(r => setTimeout(r, 2000));
    ({ error } = await attempt());
  }

  if (error) {
    console.error('Error saving schedule:', error.message);
    return false;
  }
  return true;
}
